import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import * as api from '../api';
import { pushSupported, subscribe, unsubscribe, currentSubscription } from '../pushClient';

// ─────────────────────────────────────────────────────────────
// The envelope in the top bar.
//
// Entirely separate from the bell beside it: that one watches projects for
// budget and schedule trouble, this one tells you that something of yours
// moved. They share no state and poll independently, so neither can clear or
// inflate the other's badge.
// ─────────────────────────────────────────────────────────────

const POLL_MS = 45_000;

// How each category reads at a glance. The colour is the only thing that
// separates an item needing action from one that is just news.
const TONE = {
  approval_required: { dot: '#f59e0b', label: 'Action needed' },
  returned:          { dot: '#f97316', label: 'Returned' },
  parked:            { dot: '#a855f7', label: 'Parked' },
  rejected:          { dot: '#ef4444', label: 'Rejected' },
  approved:          { dot: '#22c55e', label: 'Approved' },
  progressed:        { dot: '#3b82f6', label: 'Progress' },
  recalled:          { dot: '#eab308', label: 'Recalled' },
};
const toneFor = (c) => TONE[c] || { dot: '#94a3b8', label: 'Update' };

const timeAgo = (iso) => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

// A header action. Enabled ones read as links; disabled ones stay in place,
// greyed, rather than vanishing.
const headerActionStyle = (enabled) => ({
  background: 'none', border: 'none', padding: 0,
  color: enabled ? 'var(--accent)' : 'var(--text-muted)',
  fontWeight: 700, fontSize: 11.5, whiteSpace: 'nowrap',
  cursor: enabled ? 'pointer' : 'default',
  opacity: enabled ? 1 : 0.55, fontFamily: 'var(--font)',
});

const btnStyle = {
  position: 'relative',
  background: '#0a141c', border: '1px solid #213342', borderRadius: 11,
  padding: '8px 10px', color: '#9eb1bd', fontSize: 12.5, fontWeight: 700,
  cursor: 'pointer', fontFamily: 'var(--font)', display: 'flex', alignItems: 'center',
  transition: 'background 0.15s, border-color 0.15s',
};

export default function NotificationEnvelope({ token }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [canPopup, setCanPopup] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  // Whether THIS browser is registered for push. When it is, the service
  // worker raises every popup — including while the tab is open — so the
  // in-page fallback below must stand down or every notification arrives
  // twice.
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Ids already shown as a desktop popup. Seeded on the first load rather than
  // left empty, so signing in with a full envelope does not fire a popup for
  // every unread item at once.
  const popped = useRef(null);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const listRef = useRef(null);

  // Where to draw the panel. The top bar sits in its own stacking context
  // (position:relative, z-index:1) alongside the scrolling content, which has
  // the SAME z-index and comes later in the DOM — so it paints on top, and no
  // z-index applied INSIDE the bar can escape that. The panel is therefore
  // portalled to the body and positioned against the button, the same way
  // SearchableSelect solves it. Raising the bar's z-index instead would have
  // pushed it above Modal, which is only z-index 1000 and nested in that
  // same content context.
  const [panelPos, setPanelPos] = useState(null);

  const placePanel = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(380, window.innerWidth - 16);
    // Right-aligned to the button, then nudged back inside the viewport so a
    // narrow window cannot push the panel off-screen.
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    setPanelPos({ top: rect.bottom + 10, left, width });
  }, []);

  const loadMore = useCallback(async (currentItems) => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.listNotifications({ skip: currentItems.length, limit: 20 });
      const more = Array.isArray(res?.notifications) ? res.notifications : [];
      if (more.length > 0) {
        setItems(prev => [...prev, ...more]);
      }
      setHasMore(!!res?.has_more);
    } catch { /* silent */ }
    finally { setLoadingMore(false); }
  }, [loadingMore]);

  const load = useCallback(async () => {
    try {
      const res = await api.listNotifications({ skip: 0, limit: 20 });
      const list = Array.isArray(res?.notifications) ? res.notifications : [];
      setItems(list);
      setHasMore(!!res?.has_more);
      setUnread(res?.unread_count || 0);

      const unreadIds = list.filter((n) => !n.is_read).map((n) => n.notification_id);
      if (popped.current === null) {
        popped.current = new Set(unreadIds);   // first load: catch up silently
        return;
      }
      // Only pop for things that arrived since the last poll, and only while
      // the tab is in the background — a popup over the page you are already
      // looking at is just a second copy of the badge.
      const fresh = list.filter(
        (n) => !n.is_read && !popped.current.has(n.notification_id)
      );
      fresh.forEach((n) => popped.current.add(n.notification_id));
      if (document.hidden && canPopup === 'granted' && !pushOn) {
        fresh.slice(0, 3).forEach((n) => {
          try {
            const note = new Notification(n.title, {
              body: n.body || '',
              tag: n.notification_id,          // replaces rather than stacks
              icon: '/logo.png',
            });
            note.onclick = () => { window.focus(); note.close(); };
          } catch { /* the browser may refuse; the badge still updates */ }
        });
      }
    } catch { /* a failed poll is not worth surfacing; the next one retries */ }
  }, [canPopup, pushOn]);

  useEffect(() => {
    if (!token) return undefined;
    load();
    const iv = setInterval(load, POLL_MS);
    // Catch up the moment someone comes back to the tab, rather than making
    // them wait out the remainder of the poll interval.
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVisible); };
  }, [token, load]);

  const handleListScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || loadingMore || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
      loadMore(items);
    }
  }, [loadingMore, hasMore, loadMore, items]);

  // Reflect any subscription this browser already has. Read-only: nothing
  // here prompts, so a returning user sees the true state without being
  // asked for permission again.
  useEffect(() => {
    if (!token || !pushSupported()) return;
    let alive = true;
    currentSubscription().then((sub) => { if (alive) setPushOn(!!sub); });
    return () => { alive = false; };
  }, [token]);

  // A click on a notification raised by the service worker routes the tab
  // that the worker focused.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMessage = (e) => {
      if (e.data?.type === 'NOTIFICATION_CLICK' && e.data.route) navigate(e.data.route);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  // Close on an outside click or Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      // The panel lives outside wrapRef now that it is portalled, so a click
      // in it would otherwise read as an outside click and close the panel
      // before the item's own handler ran.
      if (panelRef.current?.contains(e.target)) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    // The panel is fixed-positioned, so it would otherwise hang in place
    // while the page scrolls or the window resizes underneath it.
    const reposition = () => placePanel();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, placePanel]);

  // Whether "Clear read" would do anything right now.
  const hasRead = items.some((x) => x.is_read);

  const toggle = async () => {
    const next = !open;
    if (next) placePanel();
    setOpen(next);
    if (next) {
      setLoading(true);
      await load();
      setLoading(false);
    }
  };

  // Permission must be asked for from a real click — browsers ignore (and
  // Chrome penalises) a request made on page load. This is that click.
  const enablePush = async () => {
    setPushBusy(true);
    try {
      const result = await subscribe();
      setCanPopup(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
      if (result === 'subscribed') {
        setPushOn(true);
      } else if (result === 'disabled' || result === 'unsupported' || result === 'failed') {
        // Push is unavailable, but a plain in-page popup may still work while
        // the tab is open, so fall back to asking for that much.
        try {
          if (typeof Notification !== 'undefined') {
            setCanPopup(await Notification.requestPermission());
          }
        } catch { /* denied */ }
      }
    } finally {
      setPushBusy(false);
    }
  };

  const disablePush = async () => {
    setPushBusy(true);
    try { await unsubscribe(); setPushOn(false); } finally { setPushBusy(false); }
  };

  const openItem = async (n) => {
    setOpen(false);
    if (!n.is_read) {
      setUnread((u) => Math.max(0, u - 1));
      setItems((prev) => prev.map((x) =>
        x.notification_id === n.notification_id ? { ...x, is_read: true } : x));
      try { await api.markNotificationsRead([n.notification_id]); } catch { /* reverts on next poll */ }
    }
    if (n.route) navigate(n.route);
  };

  const readAll = async () => {
    if (!unread) return;
    setUnread(0);
    setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
    try { await api.markAllNotificationsRead(); } catch { /* reverts on next poll */ }
  };

  // Empty the panel of everything already dealt with. Only read items go —
  // the server enforces that too, so an unread notification cannot be lost to
  // a mistimed click.
  const clearRead = async () => {
    const readCount = items.filter((x) => x.is_read).length;
    if (!readCount) return;
    setItems((prev) => prev.filter((x) => !x.is_read));
    try { await api.clearReadNotifications(); } catch { /* reverts on next poll */ }
    load();
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        onClick={toggle}
        title="Notifications"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        style={btnStyle}
        onMouseEnter={e => { e.currentTarget.style.background = '#1c3140'; e.currentTarget.style.borderColor = '#2d4456'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#0a141c'; e.currentTarget.style.borderColor = '#213342'; }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="2" />
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </svg>
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -6, right: -6,
            background: '#ef4444', color: '#fff', borderRadius: 999,
            fontSize: 10, fontWeight: 800, padding: '1px 5px', minWidth: 16, textAlign: 'center',
            lineHeight: '14px', fontVariantNumeric: 'tabular-nums',
            border: '2px solid #031540ff',
          }}>{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {open && panelPos && createPortal(
        <div ref={panelRef} style={{
          position: 'fixed', top: panelPos.top, left: panelPos.left, width: panelPos.width,
          background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 12,
          boxShadow: '0 18px 44px rgba(0,0,0,0.28)', zIndex: 9999, overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 14px', borderBottom: '1px solid var(--border-light)',
          }}>
            <span style={{ fontWeight: 800, fontSize: 13, color: 'var(--text-primary)' }}>
              Notifications{unread > 0 ? ` (${unread})` : ''}
            </span>
            {/* Both actions keep their place whether or not they currently
                apply, so neither goes missing exactly when someone looks for
                it. They grey out instead of disappearing. */}
            <span style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
              <button
                onClick={readAll}
                disabled={!unread}
                title={unread ? 'Mark every notification as read' : 'Nothing unread'}
                style={headerActionStyle(!!unread)}
              >Mark all read</button>
              <button
                onClick={clearRead}
                disabled={!hasRead}
                title={hasRead ? 'Remove read notifications from this list' : 'Nothing to clear'}
                style={headerActionStyle(hasRead)}
              >Clear read</button>
            </span>
          </div>

          {/* Device alerts. Permission is per browser per device, so this
              says "this browser" rather than implying an account setting. */}
          {canPopup !== 'unsupported' && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              padding: '10px 14px', background: 'var(--bg-hover)',
              borderBottom: '1px solid var(--border-light)',
            }}>
              <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontWeight: 600 }}>
                {canPopup === 'denied'
                  ? 'Alerts are blocked for this site in your browser settings.'
                  : pushOn
                    ? '🔔 Alerts on for this browser, even when it is closed.'
                    : 'Get alerted on this device when something needs you.'}
              </span>
              {canPopup !== 'denied' && (
                <button
                  onClick={pushOn ? disablePush : enablePush}
                  disabled={pushBusy}
                  style={{
                    background: 'none', border: 'none', flexShrink: 0,
                    color: pushOn ? 'var(--text-muted)' : 'var(--accent)',
                    fontWeight: 700, fontSize: 11.5, whiteSpace: 'nowrap',
                    cursor: pushBusy ? 'wait' : 'pointer', fontFamily: 'var(--font)',
                  }}
                >
                  {pushBusy ? '…' : pushOn ? 'Turn off' : 'Turn on'}
                </button>
              )}
            </div>
          )}

          <div ref={listRef} onScroll={handleListScroll} style={{ maxHeight: 420, overflowY: 'auto' }}>
            {loading && items.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12.5 }}>Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12.5 }}>
                Nothing yet. Requisitions needing you will show up here.
              </div>
            )}
            {items.map((n) => {
              const tone = toneFor(n.category);
              return (
                <button
                  key={n.notification_id}
                  onClick={() => openItem(n)}
                  style={{
                    display: 'flex', gap: 10, width: '100%', textAlign: 'left',
                    padding: '11px 14px', border: 'none',
                    borderBottom: '1px solid var(--border-light)',
                    background: n.is_read ? 'transparent' : 'var(--bg-hover)',
                    cursor: 'pointer', fontFamily: 'var(--font)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-elevated)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = n.is_read ? 'transparent' : 'var(--bg-hover)'; }}
                >
                  <span style={{
                    width: 8, height: 8, borderRadius: 999, background: tone.dot,
                    marginTop: 5, flexShrink: 0,
                  }} />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{
                      display: 'block', fontSize: 12.5,
                      fontWeight: n.is_read ? 600 : 800,
                      color: 'var(--text-primary)', marginBottom: 2,
                    }}>{n.title}</span>
                    {n.body && (
                      <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                        {n.body}
                      </span>
                    )}
                    <span style={{ display: 'block', fontSize: 10.5, color: 'var(--text-muted)', marginTop: 3 }}>
                      {tone.label}
                      {n.actor_name ? ` · ${n.actor_name}` : ''}
                      {` · ${timeAgo(n.created_at)}`}
                    </span>
                  </span>
                </button>
              );
            })}
            {loadingMore && (
              <div style={{ padding: '10px 14px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                Loading more…
              </div>
            )}
            {!loadingMore && hasMore && items.length > 0 && (
              <div style={{ padding: '10px 14px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 11.5 }}>
                Scroll down for more
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
